import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { NotificationType, Notifier } from '@shared/notifier/types';
import { NGXLogger } from 'ngx-logger';
import { EXPECTED_API_ERRORS, SILENT_API_ERRORS } from './silent-api-errors';

/**
 * Statussen met een eigen, verwacht afhandelpad blijven uit Sentry:
 * 0 (server onbereikbaar/offline, eigen melding), 401 (sessieverloop en
 * refresh-races) en 429 (rate limiter, eigen melding).
 */
const SENTRY_SKIP_STATUSES: readonly number[] = [0, 401, 429];

export abstract class ApiErrorHttpInterceptor<TResponseObject, TErrorObject>
  implements HttpInterceptor
{
  /** Overschrijfbaar in specs; zonder DSN (lokaal) is captureMessage een no-op. */
  protected sentry: Pick<typeof Sentry, 'captureMessage'> = Sentry;

  protected constructor(
    private readonly config: ApiHttpInterceptorConfig<TResponseObject, TErrorObject>,
    protected readonly notifier: Notifier,
    private readonly logger: NGXLogger
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRequestForThisApi(req)) {
      return next.handle(req);
    }

    const subject = new Subject<HttpResponse<any>>();

    next.handle(req).subscribe(
      httpEvent => {
        if (httpEvent instanceof HttpResponse) {
          subject.next(httpEvent);
          subject.complete();
        }
      },
      (err: any) => {
        if (err instanceof HttpErrorResponse) {
          this.logger.error(err);
          if (req.context.get(EXPECTED_API_ERRORS)) {
            if (err.status >= 500) this.captureApiError(err, req);
            subject.error(err);
            return;
          }
          this.captureApiError(err, req);
          if (!req.context.get(SILENT_API_ERRORS)) {
            this.handleError(err);
          }
          const response = new HttpResponse().clone({
            body: err.error
          });

          subject.next(response);
          subject.complete();
        }
      }
    );

    return subject;
  }

  protected notifyServerPossiblyDown(): void {
    this.notifier.error(`De server op ${this.config.url} is mogelijk niet bereikbaar!`);
  }

  /**
   * Afgehandelde API-fouten bereiken de Angular ErrorHandler nooit (ze worden
   * hieronder een gewone response met foutenvelope) en console-logs gaan bewust
   * niet naar Sentry; zonder deze expliciete capture blijft de feed er dus blind
   * voor. SILENT_API_ERRORS onderdrukt alleen de gebruikersmelding, niet de
   * capture: stille fouten zijn anders nergens buiten de browser zichtbaar.
   */
  private captureApiError(err: HttpErrorResponse, req: HttpRequest<any>): void {
    if (SENTRY_SKIP_STATUSES.includes(err.status)) return;
    const path = normalizeApiUrl(err.url ?? req.urlWithParams);
    const codes = this.envelopeErrorCodes(err.error);
    if (codes.some(code => this.config.skipCaptureCodes?.includes(code))) return;
    const traceIdentifier = this.envelopeTraceIdentifier(err.error);
    this.sentry.captureMessage(`API-fout ${err.status} op ${req.method} ${path}`, {
      level: err.status >= 500 ? 'error' : 'warning',
      fingerprint: [req.method, path, String(err.status)],
      tags: {
        http_status: String(err.status),
        ...(codes.length > 0 ? { api_error_codes: codes.join(',') } : {}),
        // Sleutel naar het Seq-event van dezelfde request (zelfde HttpContext.TraceIdentifier)
        ...(traceIdentifier ? { trace_identifier: traceIdentifier } : {})
      }
    });
  }

  /** Numerieke foutcodes uit de envelope; een afwijkende body levert gewoon geen codes op. */
  private envelopeErrorCodes(body: TResponseObject | undefined): number[] {
    return this.envelopeErrors(body)
      .map(e => this.config.errorCode(e))
      .filter(code => typeof code === 'number' && !Number.isNaN(code));
  }

  /** Eerste traceIdentifier uit de foutenlijst; zonder envelope (kale 5xx van de proxy) geen. */
  private envelopeTraceIdentifier(body: TResponseObject | undefined): string | undefined {
    const getter = this.config.traceIdentifier;
    if (!getter) return undefined;
    for (const error of this.envelopeErrors(body)) {
      const traceIdentifier = getter(error);
      if (typeof traceIdentifier === 'string' && traceIdentifier.length > 0) return traceIdentifier;
    }
    return undefined;
  }

  private envelopeErrors(body: TResponseObject | undefined): TErrorObject[] {
    try {
      const errors = body ? this.config.errors(body) : undefined;
      return Array.isArray(errors) ? errors : [];
    } catch {
      return [];
    }
  }

  private handleError(err: HttpErrorResponse): void {
    if (this.isNotFound(err)) {
      this.notifier.error(`Het pad ${err.url} werd niet gevonden!`);
      return;
    }

    if (this.isConnectionRefused(err)) {
      this.notifyServerPossiblyDown();
      return;
    }

    // Rate limiter antwoordt met een kale 429 zonder ApiResult-body.
    if (this.isTooManyRequests(err)) {
      this.notifier.error(
        'Te veel pogingen kort na elkaar. Wacht even (tot een kwartier) en probeer het opnieuw.'
      );
      return;
    }

    // Zonder parsebare foutenlijst (bv. kale 500) toch altijd iets tonen.
    const body: TResponseObject = err?.error;
    const errors = body ? this.config.errors(body) : undefined;
    if (!errors || (Array.isArray(errors) && errors.length === 0)) {
      this.notifyUnexpectedServerError(err.status);
      return;
    }
    this.notifyErrors(errors);
  }

  private notifyUnexpectedServerError(status?: number): void {
    const statusPart = status ? ` (HTTP ${status})` : '';
    this.notifier.error(
      `Er ging iets mis op de server${statusPart}. Probeer het opnieuw of contacteer support als dit blijft gebeuren.`
    );
  }

  private notifyErrors(errors: TErrorObject[] | any) {
    if (!Array.isArray(errors)) {
      if (errors['items[0].id']) {
          this.notifier.notify('Onvoorziene fout: ' + errors['items[0].id'], NotificationType.Error, {
            timeOut: this.config.timeOut,
            closeButton: this.config.closeButton
          });
      } else {
        this.notifyUnexpectedServerError();
      }
      return;
    }
    errors.forEach(e => {
      const code = this.config.errorCode(e);
      if (this.isInNotifiableRange(code)) {
        const message = this.config.message(e);
        this.notifier.notify(message, NotificationType.Error, {
          timeOut: this.config.timeOut,
          closeButton: this.config.closeButton
        });
      }
    });
  }

  private isRequestForThisApi(req: HttpRequest<any>): boolean {
    return req.url.includes(this.config.url);
  }

  private isInNotifiableRange(errorCode: number): boolean {
    return (
      this.config.notifyRanges.some(r => {
        return errorCode >= r.from && errorCode <= r.to;
      }) && !this.config.exclude?.includes(errorCode)
    );
  }

  private isConnectionRefused(err: HttpErrorResponse): boolean {
    return err.status === 0;
  }

  private isTooManyRequests(err: HttpErrorResponse): boolean {
    return err.status === 429;
  }

  private isNotFound(err: HttpErrorResponse): boolean {
    return err.status === 404;
  }
}

/**
 * Genormaliseerd pad voor Sentry-bericht en -fingerprint: querystring weg (kan
 * geboortegegevens of tokens bevatten), origin weg en id-segmenten (GUID's en
 * nummers) vervangen door {id}, zodat de feed per endpoint groepeert.
 */
export function normalizeApiUrl(url: string): string {
  const path = url.split('?')[0].replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
  return path
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{id}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');
}

export interface ApiHttpInterceptorConfig<TResponseObject, TErrorObject> {
  url: string;
  timeOut?: number;
  closeButton?: boolean;
  notifyRanges: NotifyRange[];
  exclude?: number[];
  errors: ErrorPropertyGetter<TResponseObject, TErrorObject>;
  errorCode: ErrorCodeFactory<TErrorObject>;
  message: ErrorMessageFactory<TErrorObject>;
  /** Koppelt het Sentry-event aan het Seq-event van dezelfde request (issue #45). */
  traceIdentifier?: TraceIdentifierGetter<TErrorObject>;
  /** Foutcodes die nooit naar Sentry gaan, ook niet als 5xx (bv. de platformmodus, issue #51). */
  skipCaptureCodes?: number[];
}

export interface NotifyRange {
  from: number;
  to: number;
}

export type ErrorCodeFactory<TErrorObject> = (response: TErrorObject) => number;
export type ErrorMessageFactory<TErrorObject> = (response: TErrorObject) => string;
export type TraceIdentifierGetter<TErrorObject> = (response: TErrorObject) => string | null | undefined;
export type ErrorPropertyGetter<TResponseObject, TErrorObject> = (
  response: TResponseObject
) => TErrorObject[];
